import React, { ReactElement } from "react";
import { useAuth } from "../../context/AuthContext";
import { useFeatureFlag } from "../../context/FeatureFlagProvider";
import { Role } from "../../constants/roles";

interface FeatureGateProps {
  flag: string;
  bypassRoles?: Role[];
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Gates children behind a remote feature flag (see FeatureFlagProvider).
 * Users holding any role in `bypassRoles` always get access without waiting
 * on/depending on the remote flag (e.g. internal staff should never be
 * locked out by a flag-service outage or misconfiguration).
 * While the flag is still loading for non-bypassed users, access is denied
 * (fail closed) rather than shown optimistically.
 */
const FeatureGate = ({
  flag,
  bypassRoles = [],
  fallback = <></>,
  children,
}: FeatureGateProps): ReactElement => {
  const { hasAnyRole } = useAuth();
  const { isEnabled, loading } = useFeatureFlag(flag);

  if (bypassRoles.length > 0 && hasAnyRole(bypassRoles)) {
    return <>{children}</>;
  }

  if (loading) {
    return <>{fallback}</>;
  }

  return isEnabled ? <>{children}</> : <>{fallback}</>;
};

export default FeatureGate;
